import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Briefcase, LineChart } from "lucide-react";
import tokens from "@/theme/harvest.json";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { CeresMark } from "@/components/ceres/CeresMark";
import { CeresIntro } from "@/components/ceres/CeresIntro";

const HELPS = [
  [BookOpen, "New to investing", "Plain-language answers on covered calls, yields, distributions and how to buy."],
  [Briefcase, "Advisors & professionals", "Side-by-side comparisons, leverage, fees, risk ratings and the right documents."],
  [LineChart, "Existing investors", "Distribution dates, fund changes, tax summaries and Harvest's latest research."],
];

const Aside = ({ backTo, brandLogo, brandName }) => (
  <aside className="hv-hero relative hidden flex-col justify-between overflow-hidden px-10 py-10 text-white lg:flex" data-testid="ceres-aside">
    <div className="ceres-grain-field opacity-60" aria-hidden="true" />
    <div className="relative">
      <Link to={backTo} className="inline-flex items-center gap-2 font-heading text-[12px] font-semibold uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-white" data-testid="ceres-back-link">
        <ArrowLeft size={16} aria-hidden="true" /> Back to {brandName}
      </Link>
      <div className="mt-14 flex items-center gap-4">
        <CeresMark size={84} tone="dark" className="ceres-orbit" />
        <div>
          <h1 className="font-heading text-5xl font-extrabold tracking-[0.14em]">CERES</h1>
          <p className="mt-1 font-heading text-[11px] font-semibold uppercase tracking-[0.3em] text-hv-orange">Harvest's fund guide</p>
        </div>
      </div>
      <p className="mt-8 max-w-sm text-[15px] leading-relaxed text-white/85">
        Ask about any Harvest ETF or Harvest High Income Shares™ fund. Every answer comes from harvestportfolios.com, with numbered sources and as-at dates.
      </p>
      <ul className="mt-10 space-y-6">
        {HELPS.map(([Icon, title, body], i) => (
          <li key={title} className="hv-rise flex gap-4" style={{ animationDelay: `${150 + i * 120}ms` }}>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white/70"><Icon size={18} strokeWidth={2} aria-hidden="true" /></span>
            <div>
              <p className="font-heading text-[14px] font-bold">{title}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-white/75">{body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
    <div className="relative mt-10 inline-flex w-fit items-center gap-3 bg-white px-4 py-2.5">
      <span className="font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-hv-muted">Presented by</span>
      <img src={brandLogo} alt={brandName} className="h-5 w-auto" />
    </div>
    <RansenBadge className="relative mt-4" />
  </aside>
);

const RANSEN_URL = "https://www.ransen.ca";

const RansenBadge = ({ className = "", compact = false }) => (
  <p className={`flex items-center gap-2 whitespace-nowrap font-heading font-semibold uppercase text-white/70 ${compact ? "text-[9px] tracking-[0.08em]" : "text-[10px] tracking-[0.16em]"} ${className}`} data-testid="ransen-badge">
    <span className={compact ? "" : "border border-white/40 px-2 py-1"}>{compact ? "By" : "Prototype by"} <span className="text-white">Ransen</span></span>
    <a href={RANSEN_URL} target="_blank" rel="noopener noreferrer" className="text-hv-orange underline-offset-4 hover:underline focus-visible:underline"
      data-testid="ransen-contact-link" aria-label="Contact Ransen (opens ransen.ca in a new tab)">
      {compact ? "Contact" : "Contact Ransen"}
    </a>
  </p>
);

export default function ChatPage() {
  const [params] = useSearchParams();
  const [intro, setIntro] = useState(true);
  const brand = params.get("brand") === "hhis" ? "hhis" : "etf";
  const theme = tokens.themes[brand];
  const ticker = params.get("ticker");
  const from = params.get("from");
  const backTo = from && from.startsWith("/") && !from.startsWith("/chat") ? from : brand === "hhis" ? "/high-income-shares/" : "/";
  const brandName = brand === "hhis" ? "Harvest High Income Shares" : "Harvest ETFs";
  const page = { path: from || "/chat", ticker: ticker ? ticker.toUpperCase() : null, brand };

  return (
    <div className="grid h-[100dvh] lg:grid-cols-[minmax(360px,440px)_1fr]" data-brand={brand} data-testid="chat-full-page">
      {intro && <CeresIntro onDone={() => setIntro(false)} />}
      <Aside backTo={backTo} brandLogo={theme.header_logo} brandName={brandName} />
      <main className="flex min-h-0 flex-col">
        <div className="flex items-center justify-between bg-hv-navy px-4 py-2 lg:hidden">
          <Link to={backTo} className="inline-flex items-center gap-2 font-heading text-[11px] font-semibold uppercase tracking-[0.14em] text-white" data-testid="ceres-back-link-mobile">
            <ArrowLeft size={14} aria-hidden="true" /> <span className="whitespace-nowrap">{brandName}</span>
          </Link>
          <div className="flex items-center gap-3">
            <RansenBadge compact />
            <CeresMark size={28} tone="dark" />
          </div>
        </div>
        <div className="min-h-0 flex-1">
          {!intro && <ChatPanel full page={page} theme={theme} />}
        </div>
      </main>
    </div>
  );
}
